import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Shield, ArrowRight, AlertTriangle } from 'lucide-react';
import { Card, Button, Input } from '@shared/components/ui';
import { getToken } from '@shared/services/api';

interface SetupCheckResponse {
  has_platform_admin: boolean;
}

export default function AdminSetup() {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const [isLoading, setIsLoading] = useState(true);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [setupCheckFailed, setSetupCheckFailed] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkSetupStatus = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSetupCheckFailed(false);
    try {
      const res = await fetch('/api/v1/admin/setup/check');
      if (!res.ok) throw new Error('Setup status request failed');
      const data: SetupCheckResponse = await res.json();
      if (typeof data.has_platform_admin !== 'boolean') {
        throw new Error('Setup status response was invalid');
      }
      if (data.has_platform_admin) {
        setIsSetupComplete(true);
        const destination = getToken() ? '/dashboard' : '/login';
        setTimeout(() => navigate(destination), 2000);
      } else {
        setIsSetupComplete(false);
      }
    } catch {
      setError(t('setup.errors.api'));
      setSetupCheckFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    void checkSetupStatus();
  }, [checkSetupStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(t('setup.errors.passwordMismatch'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/v1/admin/setup/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, full_name: fullName }),
      });
      if (!res.ok) throw new Error(t('setup.errors.createFailed'));
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch {
      setError(t('setup.errors.createFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark flex items-center justify-center"
        role="status"
        aria-label={t('setup.loading')}
      >
        <div
          className="animate-spin h-12 w-12 border-b-2 border-brand-500 rounded-full"
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark flex items-center justify-center p-4 selection:bg-brand-500/30">
      <div className="max-w-xl w-full animate-in fade-in zoom-in duration-700">
        <div className="text-center mb-8 sm:mb-10">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-brand-600 rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-brand-500/20 mb-6">
            <Shield className="text-white w-9 h-9 sm:w-10 sm:h-10" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
            {t('setup.title')}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
            {t('setup.subtitle')}
          </p>
        </div>

        <Card className="p-5 sm:p-8 border-surface-border-light dark:border-surface-border-dark bg-surface-card-light dark:bg-surface-card-dark shadow-xl space-y-6 sm:space-y-8">
          {setupCheckFailed ? (
            <div className="space-y-4">
              <div
                role="alert"
                className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-danger-light dark:text-state-danger-dark font-bold text-sm"
              >
                <AlertTriangle size={18} aria-hidden="true" /> {error}
              </div>
              <Button
                type="button"
                variant="secondary"
                className="w-full min-h-11"
                isLoading={isLoading}
                onClick={() => void checkSetupStatus()}
              >
                {t('setup.retry')}
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <div
                  role="alert"
                  className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-danger-light dark:text-state-danger-dark font-bold text-sm"
                >
                  <AlertTriangle size={18} aria-hidden="true" /> {error}
                </div>
              )}

              {isSetupComplete || success ? (
                <div className="text-center py-8 sm:py-12 space-y-5 sm:space-y-6">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 size={64} />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black">
                    {success ? t('setup.createdTitle') : t('setup.completeTitle')}
                  </h2>
                  <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
                    {success
                      ? t('setup.createdRedirect')
                      : t(getToken() ? 'setup.dashboardRedirect' : 'setup.loginRedirect')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    id="setup-full-name"
                    label={t('setup.fields.fullName')}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    placeholder={t('setup.fields.fullNamePlaceholder')}
                    autoComplete="name"
                    className="bg-surface-2-light dark:bg-surface-2-dark border-border-strong-light dark:border-border-strong-dark"
                  />
                  <Input
                    id="setup-email"
                    label={t('setup.fields.email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="admin@pgkb.com"
                    autoComplete="email"
                    className="bg-surface-2-light dark:bg-surface-2-dark border-border-strong-light dark:border-border-strong-dark"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      id="setup-password"
                      label={t('setup.fields.password')}
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="bg-surface-2-light dark:bg-surface-2-dark border-border-strong-light dark:border-border-strong-dark"
                    />
                    <Input
                      id="setup-confirm-password"
                      label={t('setup.fields.confirmPassword')}
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="bg-surface-2-light dark:bg-surface-2-dark border-border-strong-light dark:border-border-strong-dark"
                    />
                  </div>

                  <div className="p-4 bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20 rounded-xl">
                    <p className="text-xs font-black uppercase tracking-widest text-accent-text-light dark:text-accent-text-dark mb-1">
                      {t('setup.authorizationTitle')}
                    </p>
                    <p className="text-sm text-accent-text-light/80 dark:text-accent-text-dark/80 leading-relaxed font-medium">
                      {t('setup.authorizationDescription')}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full min-h-11 sm:h-14 text-base sm:text-lg shadow-brand-500/20"
                    isLoading={isSubmitting}
                  >
                    {t('setup.submit')} <ArrowRight className="ml-2" aria-hidden="true" />
                  </Button>
                </form>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
